import { useState, useEffect } from "react";
import GradientBorderRadius from "@/ui/gradient-border-radius";
import { IconComponent } from "@/ui/icon";
import { useToken } from "@/pages/token-v2/hooks/use-token";
import Avatar from "@/ui/avatar";
import { Balance } from "@/ui/formats";
import { Spinner } from "@/ui/spinner";

interface BuySellTurboOptionsProps {
  onBuyTurbo: (amount: string) => void;
  onSellTurbo: (percentage: string) => void;
  loading: boolean;
}

const NATIVE_STORAGE_KEY = "buySellTurboOptions_native";
const PERCENTAGE_STORAGE_KEY = "buySellTurboOptions_percentage";

const BuySellTurboOptions = ({
  onBuyTurbo,
  onSellTurbo,
  loading,
}: BuySellTurboOptionsProps) => {
  const token = useToken();

  const [nativeOptions, setNativeOptions] = useState<string[]>([]);
  const [isEditingNative, setIsEditingNative] = useState(false);
  const [editingNativeValues, setEditingNativeValues] = useState<string[]>([]);

  const [percentageOptions, setPercentageOptions] = useState<string[]>([]);
  const [isEditingPercentage, setIsEditingPercentage] = useState(false);
  const [editingPercentageValues, setEditingPercentageValues] = useState<
    string[]
  >([]);

  const [pressedBuy, setPressedBuy] = useState<string | null>(null);
  const [pressedSell, setPressedSell] = useState<string | null>(null);

  useEffect(() => {
    if (!loading) {
      setPressedBuy(null);
      setPressedSell(null);
    }
  }, [loading]);

  useEffect(() => {
    const defaultOptions = ["0.001", "0.01", "0.1", "1"];
    const savedOptions = localStorage.getItem(NATIVE_STORAGE_KEY);
    if (savedOptions) {
      setNativeOptions(JSON.parse(savedOptions));
    } else {
      setNativeOptions(defaultOptions);
      localStorage.setItem(NATIVE_STORAGE_KEY, JSON.stringify(defaultOptions));
    }
  }, []);

  useEffect(() => {
    const defaultOptions = ["10", "25", "50", "100"];
    const savedOptions = localStorage.getItem(PERCENTAGE_STORAGE_KEY);
    if (savedOptions) {
      setPercentageOptions(JSON.parse(savedOptions));
    } else {
      setPercentageOptions(defaultOptions);
      localStorage.setItem(
        PERCENTAGE_STORAGE_KEY,
        JSON.stringify(defaultOptions)
      );
    }
  }, []);

  const handleNativeOptionClick = (optionValue: string) => {
    if (!isEditingNative && !loading) {
      setPressedBuy(optionValue);
      onBuyTurbo(optionValue);
    }
  };
  const handleEditNativeClick = () => {
    if (isEditingNative) {
      const newOptions = editingNativeValues.map((val, idx) => {
        if (val.trim() === "") {
          return nativeOptions[idx] || "";
        }
        const numValue = parseFloat(val);
        if (isNaN(numValue) || numValue < 0.001) return "0.001";
        if (numValue > 100) return "100";
        return numValue.toString();
      });
      setNativeOptions(newOptions);
      localStorage.setItem(NATIVE_STORAGE_KEY, JSON.stringify(newOptions));
      setIsEditingNative(false);
    } else {
      setEditingNativeValues([...nativeOptions]);
      setIsEditingNative(true);
    }
  };
  const handleNativeInputChange = (index: number, val: string) => {
    if (val === "") {
      const newValues = [...editingNativeValues];
      newValues[index] = val;
      setEditingNativeValues(newValues);
      return;
    }
    if (!/^\d*\.?\d*$/.test(val)) return;
    if (val.startsWith(".")) {
      const newValues = [...editingNativeValues];
      newValues[index] = val;
      setEditingNativeValues(newValues);
      return;
    }
    if (val.startsWith("0")) {
      if (val === "0" || val.startsWith("0.")) {
        const newValues = [...editingNativeValues];
        newValues[index] = val;
        setEditingNativeValues(newValues);
        return;
      } else {
        return;
      }
    }
    const numValue = parseFloat(val);
    if (!isNaN(numValue)) {
      if (numValue < 0.001 && val !== "0." && val !== ".") return;
      if (numValue > 100) return;
    }
    const newValues = [...editingNativeValues];
    newValues[index] = val;
    setEditingNativeValues(newValues);
  };

  const handlePercentageOptionClick = (optionValue: string) => {
    if (!isEditingPercentage && !loading) {
      setPressedSell(optionValue);
      onSellTurbo(optionValue);
    }
  };
  const handleEditPercentageClick = () => {
    if (isEditingPercentage) {
      const newOptions = editingPercentageValues.map((val, idx) => {
        if (val.trim() === "") {
          return percentageOptions[idx] || "";
        }
        const numValue = parseFloat(val);
        if (isNaN(numValue) || numValue < 0.001) return "0.001";
        if (numValue > 100) return "100";
        return numValue.toString();
      });
      setPercentageOptions(newOptions);
      localStorage.setItem(PERCENTAGE_STORAGE_KEY, JSON.stringify(newOptions));
      setIsEditingPercentage(false);
    } else {
      setEditingPercentageValues([...percentageOptions]);
      setIsEditingPercentage(true);
    }
  };
  const handlePercentageInputChange = (index: number, val: string) => {
    if (val === "") {
      const newValues = [...editingPercentageValues];
      newValues[index] = val;
      setEditingPercentageValues(newValues);
      return;
    }
    if (!/^\d*\.?\d*$/.test(val)) return;
    if (val.startsWith(".")) {
      const newValues = [...editingPercentageValues];
      newValues[index] = val;
      setEditingPercentageValues(newValues);
      return;
    }
    if (val.startsWith("0")) {
      if (val === "0" || val.startsWith("0.")) {
        const newValues = [...editingPercentageValues];
        newValues[index] = val;
        setEditingPercentageValues(newValues);
        return;
      } else {
        return;
      }
    }
    const numValue = parseFloat(val);
    if (!isNaN(numValue)) {
      if (numValue < 0.001 && val !== "0." && val !== ".") return;
      if (numValue > 100) return;
    }
    const newValues = [...editingPercentageValues];
    newValues[index] = val;
    setEditingPercentageValues(newValues);
  };

  const nativeBalance = token?.balanceNative ?? 0;
  const tokenBalance = token?.balance ?? 0;
  const chainIcon = (
    token?.pair?.networkId === 1 ? "ethereum" : "solana"
  ) as import("@/ui/icon").IconsTypes;
  const tokenIcon = token?.icon ?? "";

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between mb-[0.313rem]">
        <div className="flex items-center gap-x-[0.313rem]">
          <span className="text-xs font-semibold">Buy</span>
          <button
            onClick={handleEditNativeClick}
            className="focus:outline-none"
            disabled={loading}
          >
            <IconComponent
              name="editPencil"
              className="w-[0.75rem] h-[0.75rem]"
            />
          </button>
        </div>
        <div className="flex items-center gap-x-[0.3125rem]">
          <IconComponent
            name={chainIcon}
            className="h-[1.1875rem] w-[1.1875rem]"
          />
          <Balance value={Number(nativeBalance)} />
        </div>
      </div>
      <div className="flex items-center gap-x-[0.3125rem]">
        {nativeOptions.map((option, index) => (
          <GradientBorderRadius
            key={`${option}-${index}`}
            radius="bigger"
            gradient={"green2"}
            className="w-full h-[1.5rem]"
            overflow
          >
            <div
              className={`flex h-full items-center justify-center gap-x-[0.188rem] cursor-pointer hover:bg-green2Bg ${loading ? "opacity-60 pointer-events-none" : ""}`}
              onClick={() => handleNativeOptionClick(option)}
            >
              {isEditingNative ? (
                <input
                  type="number"
                  value={editingNativeValues[index] || ""}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleEditNativeClick();
                    }
                  }}
                  onChange={(e) =>
                    handleNativeInputChange(index, e.target.value)
                  }
                  className="text-xs bg-transparent border-none text-center outline-none max-w-[1.875rem] w-auto"
                  autoFocus={index === 0}
                />
              ) : (
                <>
                  {loading && pressedBuy === option ? (
                    <Spinner />
                  ) : (
                    <>
                      <IconComponent
                        name="turboGreen"
                        className="w-[0.75rem] h-[0.75rem]"
                      />
                      <span className="text-xs">{option}</span>
                    </>
                  )}
                </>
              )}
            </div>
          </GradientBorderRadius>
        ))}
      </div>
      <div className="flex items-center justify-between mt-[0.938rem] mb-[0.313rem]">
        <div className="flex items-center gap-x-[0.313rem]">
          <span className="text-xs font-semibold">Sell</span>
          <button
            onClick={handleEditPercentageClick}
            className="focus:outline-none"
            disabled={loading}
          >
            <IconComponent
              name="editPencil"
              className="w-[0.75rem] h-[0.75rem]"
            />
          </button>
        </div>
        <div className="flex items-center gap-x-[0.3125rem]">
          <Avatar source={tokenIcon} size="bigger" />
          <Balance value={Number(tokenBalance)} />
        </div>
      </div>
      <div className="flex items-center gap-x-[0.3125rem]">
        {percentageOptions.map((option, index) => (
          <GradientBorderRadius
            key={`${option}-${index}`}
            radius="bigger"
            gradient={"red2"}
            className="w-full h-[1.5rem]"
            overflow
          >
            <div
              className={`flex h-full items-center justify-center gap-x-[0.188rem] cursor-pointer hover:bg-red2Bg ${loading ? "opacity-60 pointer-events-none" : ""}`}
              onClick={() => handlePercentageOptionClick(option)}
            >
              {isEditingPercentage ? (
                <input
                  type="number"
                  value={editingPercentageValues[index] || ""}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleEditPercentageClick();
                    }
                  }}
                  onChange={(e) =>
                    handlePercentageInputChange(index, e.target.value)
                  }
                  className="text-xs bg-transparent border-none text-center outline-none max-w-[1.875rem] w-auto"
                  autoFocus={index === 0}
                />
              ) : (
                <>
                  {loading && pressedSell === option ? (
                    <Spinner />
                  ) : (
                    <>
                      <IconComponent
                        name="turbo"
                        className="w-[0.75rem] h-[0.75rem]"
                      />
                      <span className="text-xs">{option}%</span>
                    </>
                  )}
                </>
              )}
            </div>
          </GradientBorderRadius>
        ))}
      </div>
    </div>
  );
};

export default BuySellTurboOptions;
